import React, { useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faCirclePlus,
  faEyeSlash,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';

import ClientContext from 'app/src/contexts/ClientContext';
import ExternalLink from 'app/src/elements/ExternalLink';
import mobileTable from 'app/src/elements/Table/MobileTable';
import RelayTable from 'app/src/elements/Table/RelayTable';
import { Title, Text, TitleLinks } from 'app/src/styles';
import { ActionText, CellText } from 'app/src/styles/table';
import { dateShort, datetimeShort } from 'app/src/utils/formatting';
import { Link } from 'app/src/utils/routing';

import EditShopifyOrderNumber from './EditShopifyOrderNumber';
import MobileRow from './MobileRow';
import { ORDERS } from './queries';
import { hoursSinceFilled } from './utils';

export const columns = [
  {
    key: 'dealer',
    value: ({ value }) => value?.label,
  },
  {
    key: 'filledAt',
    content: ({ value }) => (
      dateShort(value)
    ),
  },
  {
    key: 'quantity',
    labelTranslation: 'labels.qty',
    content: ({ value, entry }) => (
      <View style={{ flexDirection: 'row', gap: 6 }}>
        <CellText>{value}</CellText>
        {entry.hiddenFromDealerAt && (
          <FontAwesomeIcon icon={faEyeSlash} color="gray" size={16} />
        )}
      </View>
    )
  },
  {
    key: 'licenses',
    labelTranslation: 'labels.license',
    value: ({ entry }) => (
      [
        entry.newLicense?.licenseString,
        entry.usedLicense?.licenseString,
      ].filter(value => value).join(' / ')
    )
  },
  {
    key: 'serialSummaryShort',
  },
  {
    key: 'product',
    value: ({ value }) => value.name,
  },
  {
    key: 'shopifyOrderNumber',
  },
  {
    key: 'actions',
    sort: 'prevent',
    content: ({ entry }) => (
      <Link to={`/orders/${entry.id}/tags`}>
        <ActionText>View Tags</ActionText>
      </Link>
    )
  },
  {
    key: 'adminActions',
    label: 'Admin Actions',
    sort: 'prevent',
    hidden: true,
    content: ({ entry }) => (
      <View style={{ flexDirection: 'row', gap: 10 }}>
        {(hoursSinceFilled(entry) || 0) < (7 * 24) ? (
          <>
            <Link to={`/orders/${entry.id}/edit`}>
              <ActionText>Edit</ActionText>
            </Link>
            <Link to={`/orders/${entry.id}/print`}>
              <ActionText>Print</ActionText>
            </Link>
          </>
        ) : (
          <Link to={`/orders/${entry.id}`}>
            <ActionText>View</ActionText>
          </Link>
        )}
      </View>
    )
  },
];

const Orders = () => {
  const { currentUser, hasPolicy, isMobile } = useContext(ClientContext);
  const [showShopifyOrderNumber, setShowShopifyOrderNumber] = useState(false);
  const [refetch, setRefetch] = useState();

  const { t } = useTranslation();

  if (currentUser.isEmployee) {
    columns[1].content = ({ value }) => (
      datetimeShort(value) || 'Not Printed'
    );

    columns[columns.length - 1].hidden = false;

    // shopifyOrderNumber
    columns[columns.length - 3].content = ({ entry, value }) => {
      if (value) {
        return (
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <CellText>{value}</CellText>
            {entry.shopifyUrl && (
              <ExternalLink url={entry.shopifyUrl}>
                <FontAwesomeIcon
                  icon={faUpRightFromSquare}
                  size={12}
                  style={{ outlineStyle: 'none', marginRight: 3 }}
                />
              </ExternalLink>
            )}
          </View>
        );
      } else {
        return (
          <Pressable
            onPress={() => setShowShopifyOrderNumber(entry)}
            style={{ justifySelf: 'flex-end' }}
          >
            <FontAwesomeIcon icon={faCirclePlus} color="gray" size={16} />
          </Pressable>
        );
      }
    };
  }

  columns[columns.length - 2].content = ({ entry }) => (
    <Link to={`/orders/${entry.id}/tags`}>
      <ActionText>{t('actions.viewTags')}</ActionText>
    </Link>
  );

  // Search only checks dealer name, so not much use for dealer users.
  const disableSearch = !currentUser.isEmployee;

  return (
    <View>
      <Title>Orders</Title>

      {'dealer' === currentUser.role && !isMobile && (
        <Text>
          New orders do not appear in this list until they are printed.
        </Text>
      )}

      {currentUser?.isEmployee && (
        <TitleLinks isMobile={isMobile}>
          <Link to="/orders/upcoming-reorders">
            <Text>⤇ Expected Upcoming Reorders</Text>
          </Link>

          <Link to="/orders/shopify">
            <Text>⤇ Shopify Order Items</Text>
          </Link>
        </TitleLinks>
      )}

      <RelayTable
        query={ORDERS}
        queryName="orders"
        queryVariables={
          currentUser?.isEmployee ? { scopes: ['first_party'] } : {}
        }
        columns={columns}
        setRefetch={setRefetch}
        component={isMobile ? mobileTable : null}
        componentProps={{ rowComponent: MobileRow, currentUser, disableSearch }}
        disableSearch={disableSearch}
      />

      {showShopifyOrderNumber && (
        <EditShopifyOrderNumber
          order={showShopifyOrderNumber}
          setShow={setShowShopifyOrderNumber}
          refetch={refetch}
        />
      )}
    </View>
  );
};

export default Orders;
